import { ApiError } from "./api";
import { resolvePath } from "./webhook-path";

// HQ rule (TASK-233 server, TASK-234 editor). Everything between the two
// "mirror" markers copies apps/api/src/lib/webhook-mapping.ts (HQ rule form
// check + evaluation) — keep in sync. apps/admin/tests/hq-rule-eval-parity.test.ts
// runs the same cases through both and fails on any drift, so the editor's
// "x von y Proben wären HQ" counts exactly like the receiver.

// --- mirror: start ---------------------------------------------------------

export type HqCompareOp = "equals" | "in" | "contains" | "not_contains" | "answered" | "missing";

const HQ_COMPARE_OPS: readonly HqCompareOp[] = [
  "equals",
  "in",
  "contains",
  "not_contains",
  "answered",
  "missing",
];

export const HQ_EVENT_NAME = "HQLead";
/** Event id of the HQ twin = lead event id + this suffix. */
export const HQ_EVENT_ID_SUFFIX = "-hq";
/** Longest sentence for the AI proposal. */
export const HQ_SENTENCE_MAX = 1000;
export const HQ_MAX_ROUTES = 10;
export const HQ_MAX_CONDITIONS_PER_ROUTE = 10;
export const HQ_MAX_EXCLUSIONS = 10;
export const HQ_MAX_PATH_LENGTH = 300;
export const HQ_MAX_VALUES_PER_CONDITION = 50;
export const HQ_MAX_VALUE_LENGTH = 500;

export interface HqCondition {
  /** Payload path, form questions via `data.fields[label=<question>].value`. */
  path: string;
  op: HqCompareOp;
  /** Comparison texts; at least one for every op except answered/missing. */
  values?: string[];
}

export interface HqRule {
  enabled: boolean;
  event_name?: typeof HQ_EVENT_NAME;
  /** Routes: all conditions of one route must match, one route is enough. */
  routes: HqCondition[][];
  /** If any of these matches, the message is never HQ. */
  exclusions?: HqCondition[];
  /** TASK-252: "report only HQ leads as Lead to Meta" for this data source (absent = off). */
  meta_hq_only?: boolean;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function validateHqCondition(c: unknown): string | null {
  if (!isPlainObject(c)) return "ist keine gültige Bedingung.";
  if (typeof c["path"] !== "string" || !c["path"].trim()) {
    return "hat keine Frage bzw. kein Feld.";
  }
  if (c["path"].length > HQ_MAX_PATH_LENGTH) {
    return `hat eine zu lange Frage bzw. einen zu langen Feldpfad (höchstens ${HQ_MAX_PATH_LENGTH} Zeichen).`;
  }
  const op = c["op"];
  if (typeof op !== "string" || !HQ_COMPARE_OPS.includes(op as HqCompareOp)) {
    return `hat einen unbekannten Vergleich („${String(op)}“).`;
  }
  if (op !== "answered" && op !== "missing") {
    const v = c["values"];
    if (!Array.isArray(v) || v.length === 0 || v.some((x) => typeof x !== "string" || !x.trim())) {
      return "braucht mindestens einen nicht leeren Wert.";
    }
    if (v.length > HQ_MAX_VALUES_PER_CONDITION) {
      return `hat mehr als ${HQ_MAX_VALUES_PER_CONDITION} Werte (höchstens ${HQ_MAX_VALUES_PER_CONDITION}).`;
    }
    if (v.some((x: string) => x.length > HQ_MAX_VALUE_LENGTH)) {
      return `hat einen zu langen Wert (höchstens ${HQ_MAX_VALUE_LENGTH} Zeichen).`;
    }
  }
  return null;
}

/** German message for an unusable rule, `null` = valid (same as the server's 400). */
export function validateHqRule(rule: unknown): string | null {
  if (!isPlainObject(rule)) return "Die HQ-Regel muss ein Objekt sein.";
  if (typeof rule["enabled"] !== "boolean") {
    return "Der Schalter „an/aus“ der HQ-Regel fehlt oder ist ungültig.";
  }
  if (rule["event_name"] !== undefined && rule["event_name"] !== HQ_EVENT_NAME) {
    return `Der Ereignis-Name der HQ-Regel ist fest „${HQ_EVENT_NAME}“.`;
  }
  if (rule["meta_hq_only"] !== undefined && typeof rule["meta_hq_only"] !== "boolean") {
    return "Der Meta-Schalter „An Meta nur HQ-Leads als Lead melden“ der HQ-Regel kann nur an oder aus sein.";
  }
  const routes = rule["routes"];
  if (!Array.isArray(routes)) return "Die Wege der HQ-Regel müssen eine Liste sein.";
  if (routes.length > HQ_MAX_ROUTES) {
    return `Eine HQ-Regel darf höchstens ${HQ_MAX_ROUTES} Wege haben.`;
  }
  for (const [i, route] of routes.entries()) {
    if (!Array.isArray(route)) return `Weg ${i + 1} ist keine Liste von Bedingungen.`;
    if (route.length > HQ_MAX_CONDITIONS_PER_ROUTE) {
      return `Weg ${i + 1} hat mehr als ${HQ_MAX_CONDITIONS_PER_ROUTE} Bedingungen (höchstens ${HQ_MAX_CONDITIONS_PER_ROUTE}).`;
    }
    for (const [j, c] of route.entries()) {
      const err = validateHqCondition(c);
      if (err) return `Weg ${i + 1}, Bedingung ${j + 1} ${err}`;
    }
  }
  const exclusions = rule["exclusions"];
  if (exclusions !== undefined) {
    if (!Array.isArray(exclusions)) return "Die Ausschlüsse der HQ-Regel müssen eine Liste sein.";
    if (exclusions.length > HQ_MAX_EXCLUSIONS) {
      return `Eine HQ-Regel darf höchstens ${HQ_MAX_EXCLUSIONS} Ausschlüsse haben.`;
    }
    for (const [j, c] of exclusions.entries()) {
      const err = validateHqCondition(c);
      if (err) return `Ausschluss ${j + 1} ${err}`;
    }
  }
  return null;
}

const DASHES = /[‐-―−﹘﹣－]/g;

/** Case-insensitive, trimmed, one kind of dash (spaces around it dropped). */
function normalizeHq(s: string): string {
  return s
    .replace(DASHES, "-")
    // Collapse whitespace first: `\s*-\s*` on a long dash-less whitespace run is quadratic.
    .replace(/\s+/g, " ")
    .replace(/ ?- ?/g, "-")
    .trim()
    .toLowerCase();
}

function hqConditionMatches(payload: unknown, c: HqCondition): boolean {
  const v = resolvePath(payload, c.path, { joinLists: true });
  if (c.op === "answered") return v !== undefined;
  if (c.op === "missing") return v === undefined;
  // A missing question never satisfies a comparison — not even not_contains.
  if (v === undefined) return false;
  const wants = (c.values ?? []).map(normalizeHq);
  const whole = normalizeHq(v);
  if (c.op === "equals") return wants.includes(whole);
  // in: a comma answer (multi choice) hits when one entry equals one value;
  // without a comma it is plain equality.
  if (c.op === "in") {
    return v.includes(",")
      ? v.split(",").map(normalizeHq).some((e) => wants.includes(e))
      : wants.includes(whole);
  }
  // contains: substring of the whole normalised text, commas included.
  const hit = wants.some((w) => whole.includes(w));
  return c.op === "contains" ? hit : !hit;
}

/** Does this message qualify as HQ? Unusable rules (and anything that throws) mean "no HQ". */
export function matchesHqRule(payload: unknown, rule: unknown): boolean {
  try {
    if (validateHqRule(rule) !== null) return false;
    const r = rule as HqRule;
    if (!r.enabled) return false;
    if ((r.exclusions ?? []).some((c) => hqConditionMatches(payload, c))) return false;
    return r.routes.some(
      (route) => route.length > 0 && route.every((c) => hqConditionMatches(payload, c)),
    );
  } catch {
    return false;
  }
}

// --- mirror: end -----------------------------------------------------------

export const HQ_OP_OPTIONS: ReadonlyArray<{ value: HqCompareOp; label: string }> = [
  { value: "equals", label: "ist gleich" },
  { value: "in", label: "ist eines von" },
  { value: "contains", label: "enthält" },
  { value: "not_contains", label: "enthält nicht" },
  { value: "answered", label: "ist beantwortet" },
  { value: "missing", label: "fehlt" },
];

export const opTakesValues = (op: HqCompareOp): boolean => op !== "answered" && op !== "missing";

export const emptyHqRule = (): HqRule => ({
  enabled: false,
  event_name: HQ_EVENT_NAME,
  routes: [],
  exclusions: [],
});

/** The stored rule, or null when the mapping has none (or not in a usable shape). */
export function readHqRule(raw: unknown): HqRule | null {
  if (!isPlainObject(raw) || typeof raw["enabled"] !== "boolean" || !Array.isArray(raw["routes"])) {
    return null;
  }
  return raw as Record<string, unknown> & HqRule;
}

/** Stored rule is on and has the Meta switch (TASK-253). */
export function hqMetaOnly(raw: unknown): boolean {
  const r = readHqRule(raw);
  return r?.enabled === true && r.meta_hq_only === true;
}

/** What goes to the server: trimmed value entries, empty ones and empty routes dropped, fixed event name. */
export function cleanHqRule(rule: HqRule): HqRule {
  const clean = (c: HqCondition): HqCondition =>
    opTakesValues(c.op)
      ? { path: c.path.trim(), op: c.op, values: (c.values ?? []).map((v) => v.trim()).filter(Boolean) }
      : { path: c.path.trim(), op: c.op };
  return {
    enabled: rule.enabled,
    event_name: HQ_EVENT_NAME,
    routes: rule.routes.filter((r) => r.length > 0).map((r) => r.map(clean)),
    exclusions: (rule.exclusions ?? []).map(clean),
    // TASK-252: a save from the editor must never drop the Meta switch (the whole rule is replaced)
    ...(rule.meta_hq_only !== undefined ? { meta_hq_only: rule.meta_hq_only } : {}),
  };
}

export interface HqQuestion {
  label: string;
  /** `data.fields[label=<label>].value` — the path a condition stores. */
  path: string;
  /** In how many of the samples this question has an answer. */
  answered: number;
}

/**
 * Form questions found in the samples: every list of `{label, value}` objects
 * (Onepage/Adhook `data.fields`) yields one question per label.
 */
export function extractQuestions(samples: unknown[]): HqQuestion[] {
  const found = new Map<string, { label: string; path: string }>();
  const walk = (node: unknown, at: string, depth: number) => {
    if (depth > 6) return;
    if (Array.isArray(node)) {
      for (const el of node) {
        if (isPlainObject(el) && typeof el["label"] === "string" && "value" in el) {
          const label = el["label"].trim();
          if (label && !label.includes("]") && at) {
            const path = `${at}[label=${label}].value`;
            if (!found.has(path)) found.set(path, { label, path });
          }
        }
      }
    } else if (isPlainObject(node)) {
      for (const [k, v] of Object.entries(node)) walk(v, at ? `${at}.${k}` : k, depth + 1);
    }
  };
  for (const s of samples) walk(s, "", 0);
  return [...found.values()].map((q) => ({
    ...q,
    answered: samples.filter((s) => resolvePath(s, q.path, { joinLists: true }) !== undefined).length,
  }));
}

/** Plain German message for a failed save: the server's 400 text when there is one. */
export function hqSaveErrorMessage(err: unknown): string {
  if (err instanceof ApiError && isPlainObject(err.body) && typeof err.body["message"] === "string") {
    return err.body["message"];
  }
  return "Speichern fehlgeschlagen. Bitte später noch einmal versuchen.";
}

/** What `POST …/hq-rule/suggest` answers (TASK-235): the proposal plus the hints to show above the routes. */
export interface HqSuggestion {
  hq_rule: HqRule;
  notes: string[];
}

/** Plain German message for a failed suggestion: the server's 503/400 text when there is one. */
export function hqSuggestErrorMessage(err: unknown): string {
  if (err instanceof ApiError && isPlainObject(err.body) && typeof err.body["message"] === "string") {
    return err.body["message"];
  }
  return "Der Vorschlag ist gerade nicht möglich. Bitte später noch einmal versuchen oder die Regel von Hand bauen.";
}
